import type { Recipe } from '../types';

// Blender muffins. Deliberately no Greek yogurt — the usual protein-muffin
// binder — because of the texture thing. Banana, peanut butter and milk do the
// same job here and nobody can tell.

export const bananaOatProteinMuffins: Recipe = {
  slug: 'banana-oat-protein-muffins',
  title: 'Banana Oat Protein Muffins',
  meal: 'breakfast',
  category: 'baked',
  types: ['muffin', 'breakfast'],
  cuisines: [],
  traits: ['freezer-friendly', 'make-ahead', 'high-protein', 'blender'],
  yield: '24 muffins',
  activeTime: '15 min',
  totalTime: '35 min',
  freezerLife: '3 months',
  difficulty: 1,

  summary:
    'Everything goes in the Ninja, gets poured into two muffin tins, and comes out as two dozen breakfasts with about 9 grams of protein each. No flour, no mixer, one blender jar to wash.',

  flavorNotes:
    'These taste like banana bread, not like a protein bar — that distinction is the entire point and it depends on using bananas that are genuinely overripe, past the point you would want to eat one. Expect soft, moist, faintly nutty from the peanut butter, with real cinnamon warmth. They are only lightly sweet; the honey is there to round the protein powder off rather than to make them a dessert. If yours come out dry and a little rubbery, you did not do anything wrong at the mixing stage — you baked them three minutes too long.',

  ingredients: [
    {
      id: 'bananas',
      name: 'Very ripe bananas',
      total: '6 medium',
      prep: 'heavily spotted, almost black — plain yellow ones make bland muffins, see step 2 for two rescues',
      uses: [{ step: 2, amount: '6 medium', note: 'about 2½ cups mashed' }],
    },
    {
      id: 'oats',
      name: 'Old-fashioned rolled oats',
      total: '2½ cups',
      prep: 'blended into flour first',
      uses: [{ step: 1, amount: '2½ cups' }],
    },
    {
      id: 'protein-powder',
      name: 'Protein powder, vanilla or unflavored',
      total: '1 cup whey concentrate, OR ¾ cup plant-based',
      prep: 'two versions, amounts differ — whey CONCENTRATE is the one to buy (not isolate); plant-based is what the chef has now and needs less powder plus more milk, see steps 2 and 3',
      uses: [{ step: 3, amount: '1 cup (whey) or ¾ cup (plant)', note: 'about 3 scoops of whey, or 2 scoops of Garden of Life' }],
    },
    {
      id: 'eggs',
      name: 'Large eggs',
      total: '4',
      uses: [{ step: 2, amount: '4' }],
    },
    {
      id: 'peanut-butter',
      name: "Crazy Richard's peanut butter",
      total: '½ cup',
      prep: 'stirred well — the oil separates',
      uses: [{ step: 2, amount: '½ cup' }],
    },
    {
      id: 'milk',
      name: 'Whole milk',
      total: '½ cup (¾ cup with plant-based protein)',
      uses: [{ step: 2, amount: '½ cup', note: '¾ cup if the protein powder is plant-based' }],
    },
    {
      id: 'honey',
      name: 'Honey or maple syrup',
      total: '⅓ cup',
      uses: [{ step: 2, amount: '⅓ cup' }],
    },
    {
      id: 'vanilla',
      name: 'Vanilla extract',
      total: '2 tsp',
      uses: [{ step: 2, amount: '2 tsp' }],
    },
    {
      id: 'baking-soda',
      name: 'Baking soda',
      total: '2 tsp',
      uses: [{ step: 3, amount: '2 tsp' }],
    },
    {
      id: 'baking-powder',
      name: 'Baking powder',
      total: '1 tsp',
      uses: [{ step: 3, amount: '1 tsp' }],
    },
    {
      id: 'cinnamon',
      name: 'Cinnamon',
      total: '2 tsp',
      uses: [{ step: 3, amount: '2 tsp' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '1 tsp',
      uses: [{ step: 3, amount: '1 tsp' }],
    },
    {
      id: 'chocolate-chips',
      name: 'Chocolate chips',
      total: '1 cup',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy:
        'Folded in by hand at the end. Turns a sensible breakfast into one the whole house asks for.',
      uses: [{ step: 4, amount: '1 cup', note: 'folded in by hand, never blended' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Make oat flour',
      time: '1 min',
      ingredientIds: ['oats'],
      body:
        'Heat the oven to 375°F and line or grease two 12-cup muffin tins. Put the {2½ cups|oats} of rolled oats in the Ninja by themselves and run it until they look like coarse flour with no whole flakes left, about 45 seconds. Nothing else is in the jar yet.',
      tip:
        'Blending the oats dry first is the only reason this works as a one-jar recipe. Add them with the wet ingredients and they never break down — you get gritty muffins with visible flakes.',
    },
    {
      n: 2,
      title: 'Everything wet, into the same jar',
      time: '2 min',
      ingredientIds: ['bananas', 'eggs', 'peanut-butter', 'milk', 'honey', 'vanilla'],
      body:
        'Straight on top of the oat flour: the {6 medium|bananas} bananas, heavily spotted and broken into chunks, the {4|eggs} eggs, {½ cup|peanut-butter} of peanut butter, {½ cup|milk} of milk — make that {¾ cup} of milk if your protein powder is plant-based — {⅓ cup|honey} of honey, and {2 tsp|vanilla} of vanilla. Blend about 30 seconds, until it is smooth and no banana lumps are riding around the sides.',
      tip:
        'Plain yellow bananas work but the muffins come out noticeably blander, and you feel it more here than in most baking because the banana is carrying both the sweetness and the moisture. Two ways out. With two or three days: fold them into a paper bag with an apple, which traps the ethylene they give off and ripens them faster. Baking today: skins on, foil-lined sheet, 300°F for 15 to 20 minutes until the skins go black and glossy, then let them cool before peeling — hot ones are molten. The oven route gets you most of the way on sweetness and softness, though not the full aroma that slow ripening builds. Either way, if the bananas were pale, take the honey up to a scant ½ cup.',
    },
    {
      n: 3,
      title: 'Protein powder and leavening last',
      time: '1 min',
      critical: true,
      ingredientIds: ['protein-powder', 'baking-soda', 'baking-powder', 'cinnamon', 'salt'],
      body:
        'Add the protein powder — {1 cup|protein-powder} if it is whey concentrate, only {¾ cup} if it is a plant-based protein like the Garden of Life tub — then {2 tsp|baking-soda} of baking soda, {1 tsp|baking-powder} of baking powder, {2 tsp|cinnamon} of cinnamon and {1 tsp|salt} of salt. Pulse just until it disappears into the batter — five or six short pulses, no more.',
      tip:
        'Do not let the blender run here. Protein powder overworked into batter goes tight and springy, and springy is exactly what people mean when they say a protein muffin tastes like a hockey puck. Short pulses, stop early, lumps are fine. Why the two amounts: pea and rice proteins soak up far more liquid than whey does, so a full cup of plant powder plus the standard half cup of milk gives you a stiff batter and a dense, dry muffin — which is what happened on the first batch. Less powder and more milk puts the batter back where it should be, thin enough to pour.',
    },
    {
      n: 4,
      title: 'Fill the tins',
      time: '4 min',
      ingredientIds: ['chocolate-chips'],
      body:
        'Fold in the chocolate chips by hand with a spatula if you are using them. Pour the batter into the cups about three-quarters full — it is thin enough to pour straight from the blender jar, which is the nicest thing about this recipe. Let the filled tins sit on the counter for 5 minutes before they go in — that rest lets the oat flour hydrate.',
      tip:
        'Skip the rest and the muffins spread flat instead of doming. Five minutes is genuinely all it takes.',
    },
    {
      n: 5,
      title: 'Bake hot, then drop the heat',
      time: '18 min',
      critical: true,
      ingredientIds: [],
      body:
        'In at 375°F for 5 minutes, then turn the oven down to 350°F without opening the door and give them 11 to 13 minutes more. Pull them when a toothpick comes out with a few moist crumbs clinging to it — not clean. They finish cooking in the hot tin for another few minutes.',
      tip:
        'A clean toothpick means they are already overbaked. Protein powder holds far less water than flour does, so the window between done and dry is a couple of minutes wide. Err early every time — and with plant-based protein, start checking at 10 minutes instead of 11.',
    },
    {
      n: 6,
      title: 'Cool completely before packing',
      time: '30 min',
      ingredientIds: [],
      body:
        'Five minutes in the tin, then out onto a rack until they are fully at room temperature. Bagging them even slightly warm traps steam and the tops go sticky overnight.',
    },
  ],

  freezing:
    'Freeze in a single layer on a sheet pan for an hour, then pile them into a gallon bag — freezing them loose first is what keeps them from fusing into one brick. Up to 3 months. Two of these plus a banana is a real breakfast on a work morning.',

  reheat: [
    {
      method: 'Microwave from frozen',
      best: true,
      detail:
        '25 to 30 seconds and it is done. They come out warm and soft, closer to fresh-baked than they have any right to be. This is the whole reason to make two dozen at a time.',
    },
    {
      method: 'Counter thaw',
      detail:
        'Pull one out the night before and leave it in the bag on the counter. Ready by morning, no appliance involved.',
    },
    {
      method: 'Toaster oven',
      detail:
        '300°F for about 8 minutes from frozen. Slower than the microwave but the outside firms back up, which is better if you liked the original edges.',
    },
  ],

  glossary: [
    {
      term: 'whey concentrate',
      plain:
        'The cheaper, less filtered whey — and the better one for baking. Isolate is stripped further and absorbs noticeably more liquid, which is most of why protein muffins get their dry, rubbery reputation. Concentrate stays softer. On flavor: a sweetened vanilla powder stacks on top of the honey, banana and vanilla extract already in here and can push the muffins sweeter than you want, so unflavored is a perfectly good choice — and if you use it, keep the honey at a full ⅓ cup rather than cutting it.',
    },
    {
      term: 'plant-based protein',
      plain:
        'Pea, brown rice or a blend — the Garden of Life tub is this kind. Perfectly good stuff, but it is thirstier than whey: it absorbs more of the liquid in the batter and gives up less of it in the oven, so muffins made with a straight swap come out dense and a little dry. The recipe compensates with ¾ cup of powder instead of 1 and ¾ cup of milk instead of ½; protein per muffin drops by roughly a gram, which nobody will notice. For the softest result next time, buy a whey concentrate with a short label — Levels or Naked Whey at Walmart are both a single ingredient plus flavor — and go back to the standard amounts.',
    },
    {
      term: 'heavily spotted',
      plain:
        'Brown freckling over most of the skin — past the point you would want to peel one and eat it plain. Ripening is a starch-to-sugar conversion, so a spotted banana is far sweeter and more aromatic than a yellow one that looks perfectly fine on the counter. In a recipe where banana is both the sweetener and the moisture, that difference is most of the flavor.',
    },
    {
      term: 'oat flour',
      plain:
        'Rolled oats ground to a powder in a blender. It behaves close enough to whole wheat flour in a muffin, costs a third as much, and means you do not need to keep a separate bag of flour around for one recipe.',
    },
    {
      term: 'moist crumbs',
      plain:
        'What you want on the toothpick when you pull baked goods out. A few damp bits clinging to it means done. A completely clean toothpick means you have gone past done and are into dry.',
    },
    {
      term: 'hydrate',
      plain:
        'Letting a batter sit so the dry ingredients can actually absorb the liquid. With oat flour it matters more than with wheat flour — oats drink slowly, and a rested batter bakes up taller.',
    },
  ],

  inspiration: [
    { label: 'Tastes Better From Scratch — protein banana muffins', url: 'https://tastesbetterfromscratch.com/protein-banana-muffins/' },
    { label: 'iFoodReal — easy protein muffins', url: 'https://ifoodreal.com/protein-muffins/' },
  ],

  whereWeVeered:
    'Nearly every protein muffin recipe online binds with Greek yogurt. This one does not, on purpose — extra banana, peanut butter and a half cup of milk cover the same moisture and the muffins are indistinguishable. We also dropped the wheat flour entirely and went all oat flour so it stays a one-jar blender recipe, doubled it to 24 because a dozen does not last a week, and added the five-minute batter rest and the 375°F-then-350°F start, which together are what give them domed tops instead of flat ones. After the first batch came out denser than it should have, the recipe grew a second set of amounts for plant-based protein powder, since that is what ended up in the pantry.',
};
